import findRequirementCourses from './FindRequirementCourses';
import { findElectiveCourses } from './FindElectiveCourses';

const updatedCalculateProgressForMajor = (
  major,
  fetchedMajors,
  fetchedCourseData,
  finalizedSchedule,
  completedSemesters
) => {
  // Bail out if major is not defined or if the data is missing
  if (!major || !fetchedMajors || !fetchedMajors[major]) {
    return {
      takenCoursesCount: 0,
      planCoursesCount: 0,
      totCoursesCount: 0,
    };
  }

  // Aggregate all courses from each year using dynamic term keys
  let courseArray = [];
  for (let i = 1; i <= 5; i++) {
    const year = `year${i}`;
    if (finalizedSchedule[year]) {
      Object.keys(finalizedSchedule[year]).forEach((semesterKey) => {
        const courses = Array.isArray(finalizedSchedule[year][semesterKey].courses)
          ? finalizedSchedule[year][semesterKey].courses
          : [];
        courseArray.push(...courses);
      });
    }
  }

  // Get the student’s courses from fetchedCourseData that match the aggregated course IDs
  const studentCourses = fetchedCourseData
    ? fetchedCourseData.filter(course =>
        courseArray.includes(course.courseInfo_courseNumber)
      )
    : [];

  // Get all courses for the given major from fetchedMajors
  const majorCourses = Object.values(fetchedMajors[major]);

  // Exclude Milestone courses (if needed)
  const totalCourses = majorCourses.filter(
    majorCourse => majorCourse.courseType !== 'Milestone'
  ).length;

  // Determine which courses the student has taken in completed semesters,
  // iterating over dynamic term keys in completedSemesters
  const takenCoursesSet = new Set();
  Object.keys(completedSemesters).forEach((year) => {
    Object.keys(completedSemesters[year]).forEach((semesterKey) => {
      if (completedSemesters[year][semesterKey]) {
        const completedCourses =
          finalizedSchedule[year]?.[semesterKey]?.courses || [];
        completedCourses.forEach((course) => takenCoursesSet.add(course));
      }
    });
  });
  const takenCoursesArray = Array.from(takenCoursesSet);
  const takenCourses = fetchedCourseData.filter(course =>
    takenCoursesArray.includes(course.courseInfo_courseNumber)
  );

  // Identify the required major courses taken by the student
  const majorRequiredSpecificCourses = majorCourses.filter(
    majorCourse => majorCourse.courseType === 'Major Course'
  );
  const takenMajorCoursesArray = takenCourses.filter(course =>
    majorRequiredSpecificCourses.some(
      majorCourse =>
        majorCourse.courseInfo_courseNumber === course.courseInfo_courseNumber
    )
  );

  // Use helper functions to identify taken electives and requirement courses
  const takenElectiveCoursesArray = findElectiveCourses(
    majorCourses.filter(course => course.courseType === 'Elective'),
    takenCourses
  );

  const takenRequirementCoursesArray = findRequirementCourses(
    majorCourses.filter(
      course =>
        (course.courseType === 'Major Course' && !course.Requirements) ||
        course.courseType === 'Requirement'
    ),
    takenCourses,
    takenElectiveCoursesArray
  );

  // Determine all applicable courses from the student's schedule
  const allMajorCoursesArray = studentCourses.filter(course =>
    majorRequiredSpecificCourses.some(
      majorCourse =>
        majorCourse.courseInfo_courseNumber === course.courseInfo_courseNumber
    )
  );

  const allElectiveCoursesArray = findElectiveCourses(
    majorCourses.filter(course => course.courseType === 'Elective'),
    studentCourses
  );

  const allRequirementCoursesArray = findRequirementCourses(
    majorCourses.filter(
      course =>
        (course.courseType === 'Major Course' && !course.Requirements) ||
        course.courseType === 'Requirement'
    ),
    studentCourses,
    allElectiveCoursesArray
  );

  // Calculate counts for taken courses and remaining planned courses
  let takenCoursesLengths =
    takenMajorCoursesArray.length +
    takenElectiveCoursesArray.length +
    takenRequirementCoursesArray.length;

  let allCoursesLengths =
    allMajorCoursesArray.length +
    allElectiveCoursesArray.length +
    allRequirementCoursesArray.length -
    takenCoursesLengths;

  return {
    takenCoursesCount: takenCoursesLengths,
    planCoursesCount: allCoursesLengths,
    totCoursesCount: totalCourses,
  };
};

export default updatedCalculateProgressForMajor;
